import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGem } from '@/icons';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { PRO_MONTHLY_BASE, PRO_BASE } from '../pricing/subscriptionPricing';

export interface SharingUpsellProps {
    /** When ProSelfUpgrade is true, clicking "Upgrade to Pro Today!" calls this (e.g. open ProUpgradeModal). */
    onUpgradeClick?: () => void;
    /**
     * Received tab only: how many cases are waiting for this non-Pro team. Receiving is Pro-only,
     * so the count is the only thing shown about them -- no names, parties or senders.
     */
    waitingToReceiveCount?: number;
}

/**
 * SharingUpsell component - Promotional content for non-Pro users
 * Displays information about Calculist Pro features for sharing cases
 */
export const SharingUpsell: React.FC<SharingUpsellProps> = ({ onUpgradeClick, waitingToReceiveCount = 0 }) => {
    const money = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const proSelfUpgradeEnabled = useFeatureFlag('ProSelfUpgrade');

    const showUpgradeButton = proSelfUpgradeEnabled && typeof onUpgradeClick === 'function';

    return (
        <div style={{
            ...themedStyles.card.default,
            padding: theme.spacing.xl,
            margin: theme.spacing.lg,
        }}>
            <h2 style={{
                ...themedStyles.text.heading,
                fontSize: theme.typography.fontSize.xxl,
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.text.primary,
                marginBottom: theme.spacing.md,
            }}>
                {waitingToReceiveCount > 0
                    ? `You have ${waitingToReceiveCount} case${waitingToReceiveCount === 1 ? '' : 's'} waiting to be received.`
                    : 'Looking to send and receive files? Go Pro!'}
            </h2>

            <p style={{
                ...themedStyles.text.body,
                fontSize: theme.typography.fontSize.lg,
                color: theme.colors.text.primary,
                marginBottom: theme.spacing.md,
            }}>
                {waitingToReceiveCount > 0
                    ? `Upgrade to Pro to view and accept ${waitingToReceiveCount === 1 ? 'it' : 'them'}.`
                    : 'Calculist Pro users can send and receive unlimited files to users in other teams.'}
            </p>

            <ul style={{
                ...themedStyles.text.body,
                fontSize: theme.typography.fontSize.md,
                color: theme.colors.text.primary,
                marginBottom: theme.spacing.md,
                paddingLeft: theme.spacing.lg,
                lineHeight: '1.8',
            }}>
                <li>Instant delivery.</li>
                <li>Maintains a record of what was sent/received and when.</li>
                <li>User will be notified via email and in the software.</li>
                <li>Safe and secure.</li>
            </ul>

            <p style={{
                ...themedStyles.text.body,
                fontSize: theme.typography.fontSize.md,
                color: theme.colors.text.primary,
                marginBottom: theme.spacing.lg,
            }}>
                You only pay for the attorney users on your team. The cost is ${money(PRO_MONTHLY_BASE)}/attorney/mo,
                or ${money(PRO_BASE)}/attorney/yr.
            </p>

            <div style={{
                display: 'flex',
                justifyContent: 'center',
            }}>
                {showUpgradeButton ? (
                    <button
                        type="button"
                        onClick={onUpgradeClick}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.sm,
                            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                            fontSize: theme.typography.fontSize.lg,
                            fontWeight: theme.typography.fontWeight.medium,
                            color: theme.colors.text.inverse,
                            backgroundColor: theme.colors.primary,
                            border: 'none',
                            borderRadius: theme.borderRadius.md,
                            cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.colors.primaryHover ?? theme.colors.primary; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = theme.colors.primary; }}
                    >
                        <FontAwesomeIcon icon={faGem} />
                        Upgrade to Pro Today!
                    </button>
                ) : (
                    <p style={{
                        ...themedStyles.text.body,
                        fontSize: theme.typography.fontSize.lg,
                        fontWeight: theme.typography.fontWeight.medium,
                        color: theme.colors.text.primary,
                        margin: 0,
                    }}>
                        Contact support to upgrade today!
                    </p>
                )}
            </div>
        </div>
    );
};

